'use client';

/**
 * Variante: grid 4x2 card categoria + escursioni filtrate sotto.
 * Click card categoria → filtra. Card vuota → "In arrivo".
 */

import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { ArrowRight, ArrowLeft, X, Heart, Clock, MapPin, Check, Eye, Search, SlidersHorizontal, ChevronDown, Grid3X3, List, Star, Users, Globe, ShieldCheck, Map as MapIcon } from 'lucide-react';
import ExperienceMap from '@/components/catalog/ExperienceMap';
import type { ExcursionItem } from '@/app/esperienze/_views/shared';
import { CATEGORY_LABEL, durationLabel } from '@/app/esperienze/_views/shared';
import { slotsForDate, sanitizePeriods } from '@/lib/excursion-seasonal-slots';
import { TYPE_ACCENT, formatEUR, type CatalogData } from './shared';
import { getShowcaseForCategory, type ShowcasePeriod } from '@/lib/esperienze-showcase';
import { useExperienceWishlist } from '@/hooks/useExperienceWishlist';
import CategoryIcon from './CategoryIcon';
import UnifiedBookingModal from '@/components/booking/UnifiedBookingModal';

const bookingCountFor = (e: ExcursionItem, period: ShowcasePeriod): number => {
  if (period === '30d') return e.booking_count_30d ?? 0;
  if (period === '90d') return e.booking_count_90d ?? 0;
  return e.booking_count_all ?? 0;
};

// Unità prezzo leggibile dal cliente: a persona vs gruppo (intera barca).
const priceUnit = (mode?: string): string =>
  mode === 'per_person' || mode === 'tiered' ? '/pp' : mode === 'per_group' ? '/gruppo' : '';

// Prossima partenza → etichetta breve "ven 7 lug · 10:00".
const fmtNextDep = (d?: { date: string; time: string } | null): string | null => {
  if (!d?.date) return null;
  const dt = new Date(`${d.date}T12:00:00`);
  const day = dt.toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' });
  return d.time ? `${day} · ${d.time}` : day;
};

// Rating recensioni: ★ media (N) oppure 'Nuova esperienza' se senza recensioni.
const RatingChip = ({ rating, count }: { rating?: number | null; count?: number | null }) =>
  rating != null && (count ?? 0) > 0 ? (
    <span className="inline-flex items-center gap-1 text-amber-600 text-[11px] font-bold" data-cid="catalogo-card-rating">
      <Star size={11} className="fill-amber-400 text-amber-400" /> {Number(rating).toFixed(1)}
      <span className="text-slate-400 font-medium">({count})</span>
    </span>
  ) : (
    <span className="text-slate-400 text-[10px] font-semibold" data-cid="catalogo-card-rating-empty">Nuova esperienza</span>
  );

// P1.1 — chip informativi: max persone, lingue, cancellazione, cosa è incluso.
const RichChips = ({ ex }: { ex: ExcursionItem }) => {
  const maxP = ex.max_guests ?? ex.max_capacity ?? 0;
  const langs = Array.isArray(ex.languages) ? ex.languages.filter(Boolean) : [];
  const incl = Array.isArray(ex.included) ? ex.included.filter(Boolean) : [];
  if (!maxP && !langs.length && !ex.cancellation_name && !incl.length) return null;
  const chip = 'inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold';
  return (
    <div className="flex flex-wrap gap-1 mb-2" data-cid="catalogo-card-richchips">
      {maxP > 0 && <span className={`${chip} bg-slate-100 text-slate-600`}><Users size={10} /> max {maxP}</span>}
      {langs.length > 0 && <span className={`${chip} bg-slate-100 text-slate-600`}><Globe size={10} /> {langs.slice(0, 3).join('/').toUpperCase()}</span>}
      {ex.cancellation_name && <span className={`${chip} bg-slate-100 text-slate-600`}><ShieldCheck size={10} /> {ex.cancellation_name}</span>}
      {incl.slice(0, 2).map((it, i) => (
        <span key={i} className={`${chip} bg-emerald-50 text-emerald-700 max-w-[120px]`}><Check size={10} className="shrink-0" /> <span className="truncate">{it}</span></span>
      ))}
    </div>
  );
};

export default function CatalogVariantTabs({ data }: { data: CatalogData }) {
  const { excursions, activeTypes, loading, showcase } = data;
  const [activeCat, setActiveCat] = useState<string | null>(null);
  const [showCatalog, setShowCatalog] = useState(false);
  const [modalSlug, setModalSlug] = useState<string | null>(null);
  // Notifica la navbar: overlay catalogo bianco aperto → navbar solida (no bianco-su-bianco).
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('catalog:overlay', { detail: showCatalog }));
  }, [showCatalog]);
  const carouselRef = useRef<HTMLDivElement | null>(null);
  const scrollCarousel = (dir: 1 | -1) => {
    const el = carouselRef.current;
    if (!el) return;
    const card = el.querySelector<HTMLElement>('[data-cid^="esperienze-cat-card-"]');
    const step = card ? card.offsetWidth + 12 : el.clientWidth * 0.7;
    el.scrollBy({ left: dir * step, behavior: 'smooth' });
  };
  const premiumCarouselRef = useRef<HTMLDivElement | null>(null);
  const scrollPremium = (dir: 1 | -1) => {
    const el = premiumCarouselRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.88, behavior: 'smooth' });
  };
  const wishlist = useExperienceWishlist();

  // Reset catalogo quando cambia categoria o chiude
  useEffect(() => { setShowCatalog(false); }, [activeCat]);

  // Config vetrina della categoria attiva (mode/label/count/period).
  const activeShowcase = useMemo(
    () => getShowcaseForCategory(showcase, activeCat || ''),
    [showcase, activeCat],
  );

  const visible = useMemo(() => {
    if (!activeCat) return [] as ExcursionItem[];
    const inCat = excursions.filter((e) => (e.category || 'charter-custom') === activeCat);
    const { mode, count, period } = activeShowcase;
    if (mode === 'most_booked') {
      // Auto: ordina per prenotazioni confermate (finestra scelta), poi count.
      // Fallback all'ordine corrente per parità di conteggio.
      return [...inCat]
        .sort((a, b) => bookingCountFor(b, period) - bookingCountFor(a, period))
        .slice(0, count);
    }
    // Premium: scelte a mano (is_premium). Fallback a tutte se 0 premium.
    const premium = inCat.filter((e) => e.is_premium).slice(0, count);
    return premium.length > 0 ? premium : inCat.slice(0, count);
  }, [activeCat, excursions, activeShowcase]);

  // Catalogo completo (tutta la categoria, no filtro premium)
  const fullCatalog = useMemo(() => {
    if (!activeCat) return [] as ExcursionItem[];
    return excursions.filter((e) => (e.category || 'charter-custom') === activeCat);
  }, [activeCat, excursions]);

  const scrollToCatalog = () => {
    setShowCatalog(true);
    // Aspetta render catalogo, poi smooth scroll custom 1500ms ease-in-out
    requestAnimationFrame(() => {
      setTimeout(() => {
        const scroller = document.querySelector('[data-cid="esperienze-pagina-risultati"]') as HTMLElement | null;
        const target = document.getElementById('catalogo-completo');
        if (!scroller || !target) return;
        const start = scroller.scrollTop;
        const end = scroller.scrollTop + target.getBoundingClientRect().top - scroller.getBoundingClientRect().top;
        const duration = 700;
        const t0 = performance.now();
        const ease = (x: number) => 1 - Math.pow(1 - x, 3);
        const step = (now: number) => {
          const elapsed = now - t0;
          const t = Math.min(1, elapsed / duration);
          scroller.scrollTop = start + (end - start) * ease(t);
          if (t < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      }, 80);
    });
  };

  if (loading) return <div className="py-20 text-center text-white/60 text-sm">Caricamento esperienze…</div>;

  return (
    <section data-cid="esperienze-tabs" className="relative w-full min-h-full overflow-hidden pt-16 pb-12 md:pt-20 md:pb-16 px-4 md:px-8">
      {/* Pagina 1: categorie — transla via verso ALTO quando activeCat */}
      <div
        data-cid="esperienze-pagina-categorie"
        className={`absolute inset-0 flex flex-col lg:justify-center px-4 md:px-8 pt-24 md:pt-28 lg:pt-20 pb-12 md:pb-16 transition-all duration-700 ease-[cubic-bezier(0.65,0,0.35,1)] ${
          activeCat ? '-translate-y-full opacity-0 pointer-events-none' : 'translate-y-0 opacity-100'
        }`}
      >
        <div className="max-w-[1500px] mx-auto w-full">
          <div className="text-center mb-8 max-w-3xl mx-auto">
            <h2 data-cid="esperienze-categorie-title" className="font-display text-3xl md:text-5xl lg:text-6xl font-black text-white leading-[1.05] tracking-tight drop-shadow-2xl">
              Esperienze ed escursioni<br />
              scegli la <em className="font-serif italic font-medium text-cyan-300">tua</em>
            </h2>
            <p className="mt-4 text-white/75 text-sm md:text-base drop-shadow">
              {activeTypes.length} categorie · {excursions.length} esperienze prenotabili.
            </p>
          </div>

          {(() => {
            const renderCard = (t: typeof activeTypes[number], extraClass = '') => {
              const accent = TYPE_ACCENT[t.id] || TYPE_ACCENT['charter-custom'];
              const on = activeCat === t.id;
              const empty = t.count === 0;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => {
                    if (empty) return;
                    setActiveCat(on ? null : t.id);
                    if (!on) {
                      requestAnimationFrame(() => {
                        document.getElementById('esperienze')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      });
                    }
                  }}
                  disabled={empty}
                  aria-pressed={on}
                  aria-disabled={empty}
                  data-cid={`esperienze-cat-card-${t.id}`}
                  title={empty ? 'In arrivo — non ancora disponibile' : undefined}
                  className={`group relative text-left rounded-2xl bg-gradient-to-br ${accent.from} ${accent.to} backdrop-blur-md border ${on ? 'border-emerald-300/70 shadow-emerald-500/20' : 'border-white/15'} shadow-xl shadow-black/20 p-4 md:p-5 transition-all active:scale-[0.99] ${
                    empty
                      ? 'opacity-50 cursor-not-allowed pointer-events-none'
                      : 'hover:scale-[1.02] hover:border-white/30 cursor-pointer'
                  } ${extraClass}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <span className="text-emerald-200/95" aria-hidden="true">
                      <CategoryIcon id={t.id} size={32} className="md:hidden" />
                      <CategoryIcon id={t.id} size={44} className="hidden md:block" />
                    </span>
                    {empty ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-white/10 backdrop-blur text-white/70 text-[9px] font-black uppercase tracking-wider">
                        In arrivo
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-white/15 backdrop-blur text-white/95 text-[9px] font-black uppercase tracking-wider">
                        {t.count} {t.count === 1 ? 'attività' : 'attività'}
                      </span>
                    )}
                  </div>
                  <h3 data-cid={`esperienze-cat-card-title-${t.id}`} className="font-display text-white text-base md:text-lg font-black leading-tight mb-1 drop-shadow">
                    {t.label}
                  </h3>
                  <p className="text-white/65 text-[11px] md:text-xs leading-snug line-clamp-1 md:line-clamp-2 drop-shadow">
                    {t.description}
                  </p>
                  {on && (
                    <span className="absolute top-2 right-2 inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-400 text-slate-900 text-[10px] font-black">✓</span>
                  )}
                </button>
              );
            };

            return (
              <div className="mb-8">
                {/* Mobile carousel (<sm): scroll-snap horizontal con frecce overlay.
                    Card centrata (snap-center) + padding laterale 16% per lasciare
                    spazio visibile alle frecce sx/dx ai bordi schermo. */}
                <div className="relative sm:hidden">
                  <div
                    ref={carouselRef}
                    data-cid="esperienze-categorie-carousel"
                    className="flex gap-3 overflow-x-auto snap-x snap-mandatory premium-snap-noscrollbar pb-1"
                    style={{ scrollPaddingInline: '16%', paddingInline: '16%' }}
                  >
                    {activeTypes.map((t) =>
                      renderCard(t, 'shrink-0 snap-center aspect-square w-[68%] flex flex-col')
                    )}
                  </div>
                  {/* Freccia sinistra */}
                  <button
                    type="button"
                    onClick={() => scrollCarousel(-1)}
                    data-cid="esperienze-categorie-arrow-left"
                    aria-label="Scorri a sinistra"
                    className="absolute left-0 top-1/2 -translate-y-1/2 z-10 inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/90 backdrop-blur text-slate-900 shadow-lg ring-1 ring-white/40 active:scale-95 transition-transform"
                  >
                    <ArrowLeft size={14} />
                  </button>
                  {/* Freccia destra */}
                  <button
                    type="button"
                    onClick={() => scrollCarousel(1)}
                    data-cid="esperienze-categorie-arrow-right"
                    aria-label="Scorri a destra"
                    className="absolute right-0 top-1/2 -translate-y-1/2 z-10 inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/90 backdrop-blur text-slate-900 shadow-lg ring-1 ring-white/40 active:scale-95 transition-transform"
                  >
                    <ArrowRight size={14} />
                  </button>
                </div>

                {/* Desktop/tablet grid (≥sm): layout originale */}
                <div
                  data-cid="esperienze-categorie-grid"
                  className="hidden sm:grid grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4"
                >
                  {activeTypes.map((t) => renderCard(t))}
                </div>
              </div>
            );
          })()}

          <div className="flex justify-center mt-2">
            <button
              type="button"
              data-cid="esperienze-torna-home"
              onClick={() => {
                setActiveCat(null);
                if (typeof history !== 'undefined') {
                  history.replaceState(null, '', '/');
                }
                requestAnimationFrame(() => {
                  const snapRoot = document.getElementById('premium-snap-root');
                  const topSec = document.getElementById('top');
                  if (snapRoot) snapRoot.scrollTo({ top: 0, behavior: 'smooth' });
                  if (topSec) topSec.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                });
              }}
              className="group inline-flex items-center gap-1.5 text-white/70 hover:text-white text-[12px] font-bold uppercase tracking-[0.18em] transition-colors link-back-anim"
            >
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white/10 ring-1 ring-white/15 group-hover:bg-white/20 transition-colors">
                <ArrowLeft size={11} />
              </span>
              Torna in home
            </button>
          </div>
        </div>
      </div>

      {/* Pagina 2: risultati — entra dal BASSO quando activeCat.
          overscroll-contain blocca propagazione wheel verso #premium-snap-root
          (parent snap-y mandatory che altrimenti intercetta scroll interno). */}
      <div
        data-cid="esperienze-pagina-risultati"
        className={`absolute inset-0 flex flex-col overflow-y-auto overscroll-contain premium-snap-noscrollbar px-4 md:px-8 transition-all duration-700 ease-[cubic-bezier(0.65,0,0.35,1)] ${
          activeCat ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0 pointer-events-none'
        }`}
        style={{ scrollbarWidth: 'none', overscrollBehavior: 'contain' }}
      >
        {/* Overlay liquid glass support: gradient top→bottom + radial light source top-left */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 z-0"
          style={{
            backgroundImage:
              'radial-gradient(ellipse at 15% 10%, rgba(80,180,220,0.28), transparent 55%), linear-gradient(180deg, rgba(5,20,45,0.15) 0%, rgba(5,20,45,0.45) 100%)',
          }}
        />
        {activeCat && (() => {
          const cat = activeTypes.find((t) => t.id === activeCat);
          const accent = TYPE_ACCENT[activeCat] || TYPE_ACCENT['charter-custom'];
          // >4 card → carosello orizzontale (max 4 a vista) anche su desktop,
          // con frecce. ≤4 → griglia statica su 1 riga.
          const isCarousel = visible.length > 4;
          return (
            <div
              key={activeCat}
              data-cid="esperienze-risultati"
              className="relative max-w-[1400px] mx-auto w-full flex flex-col justify-start"
              style={{
                minHeight: '100svh',
                paddingInline: 'clamp(24px, 5vw, 80px)',
                paddingTop: 'clamp(84px, 6.5vw + 1vh, 116px)',
                paddingBottom: 'clamp(20px, 2vw + 1vh, 40px)',
              }}
            >
              {/* HEADER editoriale 2-col */}
              <header
                className="text-left"
                style={{ marginBottom: 'clamp(12px, 1.5vw + 0.5vh, 28px)', maxWidth: '720px' }}
              >
                <h2
                  data-cid="esperienze-risultati-title"
                  className="font-display text-white font-black leading-[1.1] tracking-tight mb-3 drop-shadow-2xl text-balance"
                  style={{ fontSize: 'clamp(22px, 2.5vw + 1.5vh, 44px)', maxWidth: '520px' }}
                >
                  Le nostre <em className="font-serif italic font-medium text-cyan-200/95">esperienze</em> in barca
                </h2>
                <p className="text-white/75 text-base md:text-[17px] leading-relaxed max-w-[520px]" data-cid="esperienze-categoria-sub">
                  {cat?.description || `${visible.length} esperienze curate per vivere il mare della Sardegna.`}
                </p>
              </header>

              <div
                data-cid="esperienze-cards-cta-group"
                className="flex flex-col"
                style={{ gap: 'clamp(16px, 1.5vw + 0.5vh, 28px)' }}
              >
              {visible.length === 0 ? (
                <div className={`max-w-md mx-auto rounded-3xl bg-gradient-to-br ${accent.from} ${accent.to} backdrop-blur-md border border-white/15 shadow-xl shadow-black/20 p-10 text-center`}>
                  <div className="inline-flex justify-center text-emerald-200/95 mb-4"><CategoryIcon id={activeCat} size={72} /></div>
                  <p className="text-white/85 text-sm mb-3">{cat?.description}</p>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur text-white text-[11px] font-black uppercase tracking-wider">
                    In arrivo…
                  </span>
                </div>
              ) : (
                <div className="relative">
                {/* Frecce overlay: sempre <lg; su lg solo in modalità carosello (>4) */}
                {visible.length > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={() => scrollPremium(-1)}
                      data-cid="esperienze-premium-arrow-left"
                      aria-label="Scorri a sinistra"
                      className={`${isCarousel ? '' : 'lg:hidden'} absolute left-1 lg:-left-3 top-1/2 -translate-y-1/2 z-20 inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/90 backdrop-blur text-slate-900 shadow-lg ring-1 ring-white/40 active:scale-95 transition-transform`}
                    >
                      <ArrowLeft size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => scrollPremium(1)}
                      data-cid="esperienze-premium-arrow-right"
                      aria-label="Scorri a destra"
                      className={`${isCarousel ? '' : 'lg:hidden'} absolute right-1 lg:-right-3 top-1/2 -translate-y-1/2 z-20 inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/90 backdrop-blur text-slate-900 shadow-lg ring-1 ring-white/40 active:scale-95 transition-transform`}
                    >
                      <ArrowRight size={16} />
                    </button>
                  </>
                )}
                <div
                  ref={premiumCarouselRef}
                  data-cid="esperienze-premium-grid"
                  className={
                    isCarousel
                      // Carosello: scroll-x + snap a tutte le larghezze, card a larghezza fissa
                      ? 'flex overflow-x-auto snap-x snap-mandatory premium-snap-noscrollbar -mx-6 px-[6%] py-3 gap-3 lg:mx-auto lg:max-w-[1400px] lg:px-0'
                      // Griglia statica 1 riga (≤4 card)
                      : `flex overflow-x-auto snap-x snap-mandatory premium-snap-noscrollbar -mx-6 px-[6%] py-3 gap-3 lg:overflow-visible lg:snap-none lg:grid lg:gap-4 lg:mx-auto lg:px-0 lg:py-0 ${
                        visible.length === 1 ? 'lg:grid-cols-1 lg:max-w-md' :
                        visible.length === 2 ? 'lg:grid-cols-2 lg:max-w-3xl' :
                        visible.length === 3 ? 'lg:grid-cols-3 lg:max-w-6xl' :
                        'lg:grid-cols-4 lg:max-w-[1400px]'
                      }`
                  }
                >
                  {visible.map((ex) => {
                    const exCat = ex.category || 'charter-custom';
                    const cp = (ex as ExcursionItem & { client_price?: number }).client_price;
                    const finalPrice = cp != null ? cp : Number(ex.price) || 0;
                    const isFav = wishlist.has(ex.id);
                    const included = Array.isArray(ex.included) ? ex.included.slice(0, 4) : [];
                    const kicker = (CATEGORY_LABEL[exCat] || exCat).toUpperCase();
                    return (
                      <div
                        key={ex.id}
                        data-cid={`esperienze-tabs-card-${ex.slug}`}
                        className={`group relative flex flex-col rounded-[28px] overflow-hidden hover:-translate-y-1.5 exp-card-anim shrink-0 w-[88%] snap-center ${
                          isCarousel ? 'lg:w-[calc((100%-36px)/4)]' : 'lg:w-auto lg:shrink'
                        }`}
                        style={{
                          background: 'rgba(255, 255, 255, 0.10)',
                          backdropFilter: 'blur(32px) saturate(180%)',
                          WebkitBackdropFilter: 'blur(32px) saturate(180%)',
                          border: '1px solid rgba(255, 255, 255, 0.18)',
                          boxShadow:
                            'inset 0 1px 0 0 rgba(255,255,255,0.45), inset 0 -1px 0 0 rgba(255,255,255,0.06), 0 30px 60px -25px rgba(5,20,45,0.55), 0 8px 24px -8px rgba(5,20,45,0.35)',
                          transition: 'transform 280ms cubic-bezier(0.22,1,0.36,1), background 280ms cubic-bezier(0.22,1,0.36,1), border-color 280ms cubic-bezier(0.22,1,0.36,1), box-shadow 280ms cubic-bezier(0.22,1,0.36,1)',
                        }}
                        onMouseEnter={(e) => {
                          const t = e.currentTarget;
                          t.style.background = 'rgba(255, 255, 255, 0.14)';
                          t.style.borderColor = 'rgba(255, 255, 255, 0.28)';
                          t.style.boxShadow = 'inset 0 1px 0 0 rgba(255,255,255,0.55), inset 0 -1px 0 0 rgba(255,255,255,0.08), 0 40px 80px -25px rgba(5,20,45,0.65), 0 12px 28px -8px rgba(5,20,45,0.4)';
                        }}
                        onMouseLeave={(e) => {
                          const t = e.currentTarget;
                          t.style.background = 'rgba(255, 255, 255, 0.10)';
                          t.style.borderColor = 'rgba(255, 255, 255, 0.18)';
                          t.style.boxShadow = 'inset 0 1px 0 0 rgba(255,255,255,0.45), inset 0 -1px 0 0 rgba(255,255,255,0.06), 0 30px 60px -25px rgba(5,20,45,0.55), 0 8px 24px -8px rgba(5,20,45,0.35)';
                        }}
                      >
                        {/* Noise overlay 3% — realismo grain */}
                        <div
                          aria-hidden
                          className="pointer-events-none absolute inset-0 z-[1] opacity-[0.03] mix-blend-overlay"
                          style={{
                            backgroundImage:
                              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
                          }}
                        />

                        {/* IMAGE block: inset 6px dal bordo, ratio 4/3 */}
                        <Link
                          href={`/esperienze/${ex.slug}`}
                          className="relative block m-[6px] aspect-[16/9] overflow-hidden rounded-[22px] z-[2]"
                          data-cid={`esperienze-tabs-img-${ex.slug}`}
                        >
                          {ex.hero_image ? (
                            <div
                              className="absolute inset-0 bg-cover bg-center transition-transform duration-[280ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.04]"
                              style={{ backgroundImage: `url(${ex.hero_image})` }}
                            />
                          ) : (
                            <div className="absolute inset-0 bg-white/5 flex items-center justify-center text-white/70">
                              <CategoryIcon id={exCat} size={72} />
                            </div>
                          )}

                          {/* Badge top-left: in mode='premium' sulle is_premium,
                              in mode='most_booked' su tutte le card in vetrina.
                              Testo = label configurabile (es. "Più prenotate"). */}
                          {(activeShowcase.mode === 'most_booked' || ex.is_premium) && (
                            <span
                              className="absolute top-3 left-3 z-10 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider text-slate-900"
                              style={{
                                background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                              }}
                            >
                              ★ {activeShowcase.label}
                            </span>
                          )}

                          {/* Clock duration badge bottom-left */}
                          {ex.duration_minutes > 0 && (
                            <span
                              className="absolute bottom-3 left-3 z-10 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold text-white"
                              style={{
                                background: 'rgba(15, 25, 40, 0.55)',
                                backdropFilter: 'blur(12px)',
                                WebkitBackdropFilter: 'blur(12px)',
                                border: '1px solid rgba(255,255,255,0.15)',
                              }}
                            >
                              <Clock size={11} /> {durationLabel(ex.duration_minutes)}
                            </span>
                          )}
                        </Link>

                        {/* Wishlist heart top-right — 40px circular + pop bounce */}
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); e.preventDefault(); wishlist.toggle(ex.id); }}
                          aria-pressed={isFav}
                          aria-label={isFav ? 'Rimuovi dai preferiti' : 'Aggiungi ai preferiti'}
                          data-cid={`esperienze-wishlist-${ex.slug}`}
                          className="absolute top-[18px] right-[18px] z-20 inline-flex items-center justify-center w-10 h-10 rounded-full transition-all"
                          style={{
                            background: isFav ? 'rgba(244, 63, 94, 0.95)' : 'rgba(255,255,255,0.22)',
                            backdropFilter: 'blur(14px)',
                            WebkitBackdropFilter: 'blur(14px)',
                            border: `1px solid ${isFav ? 'rgba(244,63,94,0.55)' : 'rgba(255,255,255,0.3)'}`,
                          }}
                        >
                          <Heart
                            key={String(isFav)}
                            size={16}
                            color="#fff"
                            fill={isFav ? '#fff' : 'none'}
                            className="exp-heart-pop"
                          />
                        </button>

                        {/* CONTENT block */}
                        <div className="relative z-[2] flex-1 flex flex-col px-7 pt-4 pb-4">
                          {/* Kicker */}
                          <div
                            className="text-[11px] font-semibold uppercase mb-2.5"
                            style={{ letterSpacing: '0.12em', color: 'rgba(255,255,255,0.65)' }}
                          >
                            {kicker}
                          </div>

                          {/* Title */}
                          <Link
                            href={`/esperienze/${ex.slug}`}
                            className="block mb-1.5 no-underline"
                          >
                            <h4
                              data-cid={`esperienze-premium-card-title-${ex.slug}`}
                              className="font-bold text-white tracking-tight line-clamp-2"
                              style={{ fontSize: '22px', lineHeight: '1.2' }}
                            >
                              {ex.title}
                            </h4>
                          </Link>

                          {/* Location row */}
                          {ex.meeting_point && (
                            <div className="inline-flex items-center gap-1.5 mb-3 min-w-0">
                              <MapPin size={16} className="shrink-0" color="rgba(255,255,255,0.75)" />
                              <span className="truncate text-[14px] font-medium" style={{ color: 'rgba(255,255,255,0.75)' }}>
                                {ex.meeting_point}
                              </span>
                            </div>
                          )}

                          {/* Feature list 2-col */}
                          {included.length > 0 && (
                            <ul
                              className="grid grid-cols-1 sm:grid-cols-2 gap-y-2.5 gap-x-4 mb-3"
                              data-cid={`esperienze-included-${ex.slug}`}
                            >
                              {included.map((feat, i) => (
                                <li key={i} className="inline-flex items-start gap-2 text-[12.5px] font-medium leading-snug" style={{ color: 'rgba(255,255,255,0.88)' }}>
                                  <Check size={16} className="shrink-0 mt-[2px]" color="#34D399" strokeWidth={2.5} />
                                  <span className="line-clamp-1">{feat}</span>
                                </li>
                              ))}
                            </ul>
                          )}

                          {/* FOOTER row: hairline + price + CTA */}
                          <div className="mt-auto pt-2" style={{ borderTop: '1px solid rgba(255,255,255,0.14)', marginTop: '10px' }}>
                            <div className="flex items-center justify-between gap-3">
                              {/* Price block */}
                              <div className="flex flex-col">
                                {finalPrice > 0 ? (
                                  <>
                                    <span
                                      className="text-[12px] font-semibold uppercase"
                                      style={{ letterSpacing: '0.08em', color: 'rgba(255,255,255,0.65)' }}
                                    >
                                      Da
                                    </span>
                                    <span className="font-bold text-white tabular-nums" style={{ fontSize: '28px', lineHeight: 1 }}>
                                      {formatEUR(finalPrice)}
                                    </span>
                                    <span className="font-semibold text-white/70 text-[13px] ml-0.5">{priceUnit(ex.pricing_mode)}</span>
                                  </>
                                ) : (
                                  <span className="font-semibold text-white" style={{ fontSize: '20px' }}>
                                    Su richiesta
                                  </span>
                                )}
                              </div>

                              {/* CTA pill — apre UnifiedBookingModal (lazy-fetch) */}
                              <button
                                type="button"
                                data-cid={`card-prenota-${ex.slug}`}
                                onClick={() => setModalSlug(ex.slug)}
                                className="inline-flex items-center gap-2 text-white font-semibold text-[15px] no-underline cta-pill-anim"
                                style={{
                                  padding: '14px 24px',
                                  borderRadius: '999px',
                                  background: 'linear-gradient(135deg, #10B981 0%, #059669 50%, #047857 100%, #059669 150%, #10B981 200%)',
                                }}
                              >
                                Prenota
                                <ArrowRight size={16} className="transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:translate-x-0.5" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                </div>
              )}

              {/* Footer azioni sotto le card premium: Indietro + Vedi tutte.
                  Ancorato al bottom via justify-between sul parent (sempre
                  visibile nel viewport, no scroll user-initiated richiesto). */}
              <div
                data-cid="esperienze-cta-row"
                className="flex flex-col sm:flex-row items-center justify-between gap-4"
              >
                <button
                  type="button"
                  onClick={() => setActiveCat(null)}
                  data-cid="esperienze-back"
                  className="group inline-flex items-center gap-1.5 text-white/70 hover:text-white text-[12px] font-bold uppercase tracking-[0.18em] transition-colors link-back-anim"
                >
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white/10 ring-1 ring-white/15 group-hover:bg-white/20 transition-colors">
                    <ArrowLeft size={11} />
                  </span>
                  Indietro
                </button>
                <Link
                  href="/esperienze"
                  data-cid="esperienze-vedi-tutte"
                  className="inline-flex items-center gap-1 text-white font-medium text-[15px] hover:text-cyan-200 transition-colors link-underline-grow"
                >
                  Vedi tutte le esperienze
                  <ArrowRight size={16} />
                </Link>
              </div>
              </div>
            </div>
          );
        })()}

      </div>

      {/* ╔══════════════════════════════════════════════════════════════════
            CATALOGO COMPLETO — pagina che SLIDA da DESTRA su "Vedi tutte".
            Overlay a livello sezione: scroll verticale interno → footer.
         ╚══════════════════════════════════════════════════════════════════ */}
      {activeCat && fullCatalog.length > 0 && (
        <div
          data-cid="esperienze-pagina-catalogo"
          className={`absolute inset-0 z-40 overflow-y-auto bg-white transition-all duration-[800ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
            showCatalog ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0 pointer-events-none'
          }`}
        >
          <FullCatalogSection
            items={fullCatalog}
            categoryLabel={activeTypes.find((t) => t.id === activeCat)?.label || activeCat}
            wishlist={wishlist}
            onClose={() => setShowCatalog(false)}
          />
        </div>
      )}

      {/* Unified Booking Modal — lazy-fetch per slug escursione */}
      <UnifiedBookingModal
        open={!!modalSlug}
        onClose={() => setModalSlug(null)}
        assetType="excursion"
        assetId={modalSlug ?? undefined}
      />
    </section>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   FULL CATALOG SECTION — sotto Premium Choice
   ══════════════════════════════════════════════════════════════════════════ */

interface FullCatalogProps {
  items: ExcursionItem[];
  categoryLabel: string;
  wishlist: ReturnType<typeof useExperienceWishlist>;
  onClose?: () => void;
}

function FullCatalogSection({ items, categoryLabel, wishlist, onClose }: FullCatalogProps) {
  const [query, setQuery] = useState('');
  const [sortBy, setSortBy] = useState<'recent' | 'price-asc' | 'price-desc' | 'rating'>('recent');
  const [priceMax, setPriceMax] = useState<number | null>(null);
  const [durationBucket, setDurationBucket] = useState<'all' | 'short' | 'half' | 'full'>('all');
  const [dateFilter, setDateFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [minPersons, setMinPersons] = useState(0);
  const [langFilter, setLangFilter] = useState('');
  const [sortOpen, setSortOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list' | 'map'>('grid');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [page, setPage] = useState(1);

  const sortOptions: { value: 'recent' | 'price-asc' | 'price-desc' | 'rating'; label: string }[] = [
    { value: 'recent', label: 'Più recenti' },
    { value: 'rating', label: 'Migliori recensioni' },
    { value: 'price-asc', label: 'Prezzo crescente' },
    { value: 'price-desc', label: 'Prezzo decrescente' },
  ];
  const currentSortLabel = sortOptions.find(o => o.value === sortBy)?.label ?? 'Ordina';

  const maxPrice = useMemo(() => {
    if (items.length === 0) return 500;
    return Math.max(...items.map((e) => {
      const cp = (e as ExcursionItem & { client_price?: number }).client_price;
      return cp != null ? cp : Number(e.price) || 0;
    }));
  }, [items]);

  // Opzioni filtro derivate dagli item (luogo/categoria/lingua).
  const facets = useMemo(() => {
    const loc = new Map<string, number>();
    const cat = new Map<string, number>();
    const lang = new Set<string>();
    for (const e of items) {
      const l = (e.meeting_point || 'Altrove').trim();
      loc.set(l, (loc.get(l) ?? 0) + 1);
      if (e.category) cat.set(e.category, (cat.get(e.category) ?? 0) + 1);
      (Array.isArray(e.languages) ? e.languages : []).forEach((x) => x && lang.add(String(x)));
    }
    return {
      locations: [...loc.entries()].sort((a, b) => b[1] - a[1]),
      categories: [...cat.keys()].sort(),
      languages: [...lang].sort(),
    };
  }, [items]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let res = items.filter((ex) => {
      const cp = (ex as ExcursionItem & { client_price?: number }).client_price ?? (Number(ex.price) || 0);
      if (priceMax !== null && cp > priceMax) return false;
      if (durationBucket !== 'all') {
        const d = ex.duration_minutes || 0;
        if (durationBucket === 'short' && d > 180) return false;
        if (durationBucket === 'half'  && (d < 180 || d > 360)) return false;
        if (durationBucket === 'full'  && d < 360) return false;
      }
      if (locationFilter && (ex.meeting_point || 'Altrove').trim() !== locationFilter) return false;
      if (categoryFilter && ex.category !== categoryFilter) return false;
      if (minPersons > 0 && (ex.max_guests ?? ex.max_capacity ?? 0) < minPersons) return false;
      if (langFilter && !(Array.isArray(ex.languages) ? ex.languages : []).map(String).includes(langFilter)) return false;
      if (dateFilter) {
        const periods = sanitizePeriods((ex as { seasonal_periods?: unknown }).seasonal_periods);
        if (slotsForDate(periods, dateFilter.slice(5, 10)).length === 0) return false;
      }
      if (!q) return true;
      const hay = [ex.title, ex.description || '', ex.owner?.company_name || '', ex.meeting_point || ''].join(' ').toLowerCase();
      return hay.includes(q);
    });
    if (sortBy === 'rating') {
      res = [...res].sort((a, b) => (Number(b.rating) || 0) - (Number(a.rating) || 0) || (Number(b.reviews_count) || 0) - (Number(a.reviews_count) || 0));
    } else if (sortBy === 'price-asc' || sortBy === 'price-desc') {
      res = [...res].sort((a, b) => {
        const ap = (a as ExcursionItem & { client_price?: number }).client_price ?? (Number(a.price) || 0);
        const bp = (b as ExcursionItem & { client_price?: number }).client_price ?? (Number(b.price) || 0);
        return sortBy === 'price-asc' ? ap - bp : bp - ap;
      });
    }
    return res;
  }, [items, query, priceMax, durationBucket, dateFilter, locationFilter, categoryFilter, minPersons, langFilter, sortBy]);

  // Righe intere: 4 colonne (filtro chiuso) / 3 (aperto). Mostro multipli esatti + "Carica altre".
  const cols = viewMode === 'list' ? 1 : (filtersOpen ? 3 : 4);
  const rowsStep = viewMode === 'list' ? 5 : 2;   // lista: 5 righe/pagina · griglia: 2
  useEffect(() => { setPage(1); }, [query, priceMax, durationBucket, dateFilter, locationFilter, categoryFilter, minPersons, langFilter, sortBy, filtersOpen, viewMode]);
  // Raggruppa per location (meeting_point): luogo come chiave primaria, ordinamento
  // utente come secondario (sort stabile) → risultati separati per LUOGO.
  const grouped = useMemo(() => {
    const loc = (e: ExcursionItem) => (e.meeting_point || 'Altrove').trim();
    return [...filtered].sort((a, b) => loc(a).localeCompare(loc(b)));
  }, [filtered]);
  const pageSize = cols * rowsStep * page;
  const visible = grouped.length <= pageSize ? grouped : grouped.slice(0, pageSize);
  const hasMore = grouped.length > visible.length;

  return (
    <section
      id="catalogo-completo"
      data-cid="esperienze-catalogo-completo"
      className="relative -mx-4 md:-mx-8 flex flex-col"
      style={{
        background: 'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.55) 10%, rgba(255,255,255,0.78) 22%, #ffffff 45%, #ffffff 100%)',
      }}
    >

      <div
        className="relative max-w-[1280px] mx-auto w-full"
        style={{
          paddingInline: 'clamp(24px, 5vw, 80px)',
          paddingTop: 'clamp(108px, 11vh, 128px)',
          paddingBottom: 'clamp(48px, 6vw + 2vh, 120px)',
        }}
      >
        {/* Header — Indietro a SINISTRA (sopra), poi titolo. */}
        <div data-cid="esperienze-fullcatalog-header" className="mb-3">
          <div className="flex flex-nowrap items-center justify-between gap-3 mb-4">
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                data-cid="esperienze-catalogo-back"
                className="group inline-flex items-center gap-1.5 text-slate-500 hover:text-slate-900 text-[12px] font-bold uppercase tracking-[0.18em] transition-colors link-back-anim"
              >
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-slate-100 ring-1 ring-slate-200 group-hover:bg-slate-200 transition-colors">
                  <ArrowLeft size={11} />
                </span>
                Indietro
              </button>
            )}
            <span data-cid="esperienze-fullcatalog-kicker" className="shrink-0 inline-flex items-center px-3 py-1 rounded-full bg-slate-100 ring-1 ring-slate-200 text-slate-600 text-[10px] font-bold uppercase tracking-[0.22em]">
              Catalogo completo
            </span>
          </div>
          <div className="max-w-2xl">
            <h3
              data-cid="esperienze-fullcatalog-title"
              className="font-display text-slate-900 font-black leading-[1.1] tracking-tight whitespace-nowrap"
              style={{ fontSize: 'clamp(24px, 2.8vw + 1.5vh, 48px)' }}
            >
              Tutte le esperienze <em className="font-serif italic font-medium text-cyan-700">{categoryLabel.toLowerCase()}</em>
            </h3>
            <p className="mt-3 text-slate-500 text-sm md:text-base">
              {items.length} {items.length === 1 ? 'esperienza disponibile' : 'esperienze disponibili'}. Filtra per durata e prezzo.
            </p>
          </div>
        </div>

        {/* Toolbar (stessa tipologia di boats-catalog-toolbar) */}
        <div
          className="mb-4 bg-white rounded-2xl border border-slate-200 p-2 shadow-[0_10px_30px_-12px_rgba(15,23,42,0.12)]"
          data-cid="excursions-catalog-toolbar"
        >
          <div
            className="flex flex-col lg:flex-row items-stretch lg:items-center gap-2"
            data-cid="excursions-catalog-toolbar-row"
          >
            {/* Search input */}
            <div className="relative flex-1 min-w-0" data-cid="excursions-catalog-toolbar-search">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={`Cerca tra le esperienze ${categoryLabel.toLowerCase()}…`}
                className="w-full pl-12 pr-4 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-400 transition-all"
              />
            </div>

            {/* Filtro data: disponibili il… */}
            <div className="relative shrink-0" data-cid="excursions-catalog-toolbar-date">
              <input
                type="date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                title="Disponibili il…"
                className="px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-700 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-400"
              />
              {dateFilter && (
                <button type="button" onClick={() => setDateFilter('')} data-cid="excursions-catalog-toolbar-date-clear" title="Azzera data" className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-slate-700 text-white text-[10px] leading-none flex items-center justify-center">×</button>
              )}
            </div>

            {/* Sort dropdown */}
            <div className="relative" data-cid="excursions-catalog-toolbar-sort">
              <button
                type="button"
                onClick={() => setSortOpen(!sortOpen)}
                className="flex items-center justify-between gap-3 px-4 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-700 font-medium text-sm hover:bg-slate-100 transition-colors min-w-[180px]"
              >
                <span className="truncate">{currentSortLabel}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${sortOpen ? 'rotate-180' : ''}`} />
              </button>

              {sortOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setSortOpen(false)} />
                  <div
                    className="absolute right-0 top-full mt-2 w-52 bg-white rounded-xl shadow-xl border border-slate-200 py-2 z-20"
                    data-cid="excursions-catalog-toolbar-sort-menu"
                  >
                    {sortOptions.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => {
                          setSortBy(option.value);
                          setSortOpen(false);
                        }}
                        className={`w-full px-4 py-2.5 text-left text-sm transition-colors ${
                          sortBy === option.value
                            ? 'bg-cyan-50 text-cyan-700 font-semibold'
                            : 'text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* View mode toggle */}
            <div
              className="flex items-center bg-slate-100 rounded-xl p-1"
              data-cid="excursions-catalog-toolbar-viewmode"
            >
              <button
                type="button"
                onClick={() => setViewMode('grid')}
                data-cid="excursions-catalog-toolbar-viewmode-grid"
                className={`p-1.5 rounded-lg transition-all ${
                  viewMode === 'grid'
                    ? 'bg-white text-cyan-700 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
                title="Griglia"
              >
                <Grid3X3 className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={() => setViewMode('list')}
                data-cid="excursions-catalog-toolbar-viewmode-list"
                className={`p-1.5 rounded-lg transition-all ${
                  viewMode === 'list'
                    ? 'bg-white text-cyan-700 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
                title="Lista"
              >
                <List className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={() => setViewMode('map')}
                data-cid="excursions-catalog-toolbar-viewmode-map"
                className={`p-1.5 rounded-lg transition-all ${
                  viewMode === 'map'
                    ? 'bg-white text-cyan-700 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
                title="Mappa"
              >
                <MapIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Results count */}
            <div
              className="text-sm text-slate-500 font-medium whitespace-nowrap"
              data-cid="excursions-catalog-toolbar-results-count"
            >
              <span className="text-cyan-700 font-bold">{filtered.length}</span>{' '}
              {filtered.length === 1 ? 'esperienza trovata' : 'esperienze trovate'}
            </div>
          </div>
        </div>

        {/* Layout: sidebar filtri + grid */}
        <div className="flex gap-5 items-start">
          {/* Filtro catalogo: collassato a SX di default (rail verticale), espandibile. */}
          {!filtersOpen && (
            <button
              type="button"
              data-cid="esperienze-catalogo-filters-toggle"
              onClick={() => setFiltersOpen(true)}
              className="shrink-0 self-start flex flex-col items-center gap-3 px-2.5 py-4 rounded-2xl bg-white border border-slate-200 shadow-sm hover:bg-slate-50 text-slate-600 transition-colors"
              title="Apri filtri"
            >
              <SlidersHorizontal size={16} />
              <span className="[writing-mode:vertical-rl] rotate-180 text-[11px] font-bold uppercase tracking-[0.18em]">Filtri catalogo</span>
            </button>
          )}
          {filtersOpen && (
          <aside
            data-cid="esperienze-catalogo-filters"
            className="w-[240px] shrink-0 lg:sticky lg:top-4 self-start rounded-2xl p-5 bg-white border border-slate-200 shadow-sm"
          >
            <div className="flex items-center justify-between mb-4">
              <span className="text-slate-700 text-[11px] font-bold uppercase tracking-[0.18em] inline-flex items-center gap-2">
                <SlidersHorizontal size={13} /> Filtri catalogo
              </span>
              <button type="button" onClick={() => setFiltersOpen(false)} className="text-slate-400 hover:text-slate-700" title="Chiudi filtri"><X size={16} /></button>
            </div>

            {/* Durata */}
            <div className="mb-5">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">Durata</div>
              <div className="space-y-1.5">
                {([
                  { id: 'all',   label: 'Tutte' },
                  { id: 'short', label: '< 3h' },
                  { id: 'half',  label: 'Mezza giornata (3-6h)' },
                  { id: 'full',  label: 'Giornata intera (6h+)' },
                ] as const).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setDurationBucket(opt.id)}
                    className={`w-full text-left px-2.5 py-1.5 rounded-lg text-[12px] font-medium transition-colors ${
                      durationBucket === opt.id
                        ? 'bg-cyan-100 text-cyan-800 ring-1 ring-cyan-300'
                        : 'text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Prezzo max */}
            <div className="mb-5">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">Prezzo massimo</div>
              <input
                type="range"
                min={0}
                max={maxPrice}
                step={10}
                value={priceMax ?? maxPrice}
                onChange={(e) => setPriceMax(Number(e.target.value) >= maxPrice ? null : Number(e.target.value))}
                className="w-full accent-cyan-500"
              />
              <div className="flex justify-between text-[10px] text-slate-500 mt-1 font-medium">
                <span>0 €</span>
                <span>{priceMax !== null ? `≤ ${formatEUR(priceMax)}` : `Qualsiasi (${formatEUR(maxPrice)})`}</span>
              </div>
            </div>

            {/* Luogo */}
            {facets.locations.length > 1 && (
              <div className="mb-5">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">Luogo</div>
                <select
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                  data-cid="esperienze-filter-location"
                  className="w-full px-2.5 py-1.5 rounded-lg text-[12px] bg-slate-50 border border-slate-200 text-slate-700 focus:outline-none focus:ring-2 focus:ring-cyan-500/20"
                >
                  <option value="">Tutti i luoghi</option>
                  {facets.locations.map(([loc, n]) => <option key={loc} value={loc}>{loc} ({n})</option>)}
                </select>
              </div>
            )}

            {/* Categoria */}
            {facets.categories.length > 1 && (
              <div className="mb-5">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">Categoria</div>
                <div className="flex flex-wrap gap-1.5" data-cid="esperienze-filter-category">
                  {facets.categories.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setCategoryFilter(categoryFilter === c ? '' : c)}
                      className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors ${categoryFilter === c ? 'bg-cyan-100 text-cyan-800 ring-1 ring-cyan-300' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                    >
                      {CATEGORY_LABEL[c] ?? c}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Persone */}
            <div className="mb-5">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">Persone</div>
              <div className="flex flex-wrap gap-1.5" data-cid="esperienze-filter-persons">
                {[0, 2, 4, 6, 8].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setMinPersons(n)}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors ${minPersons === n ? 'bg-cyan-100 text-cyan-800 ring-1 ring-cyan-300' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                  >
                    {n === 0 ? 'Tutte' : `${n}+`}
                  </button>
                ))}
              </div>
            </div>

            {/* Lingua */}
            {facets.languages.length > 0 && (
              <div className="mb-5">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">Lingua</div>
                <div className="flex flex-wrap gap-1.5" data-cid="esperienze-filter-language">
                  {facets.languages.map((l) => (
                    <button
                      key={l}
                      type="button"
                      onClick={() => setLangFilter(langFilter === l ? '' : l)}
                      className={`px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase transition-colors ${langFilter === l ? 'bg-cyan-100 text-cyan-800 ring-1 ring-cyan-300' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                    >
                      {l}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Reset */}
            {(query || priceMax !== null || durationBucket !== 'all' || dateFilter || locationFilter || categoryFilter || minPersons > 0 || langFilter || sortBy !== 'recent') && (
              <button
                type="button"
                onClick={() => { setQuery(''); setPriceMax(null); setDurationBucket('all'); setDateFilter(''); setLocationFilter(''); setCategoryFilter(''); setMinPersons(0); setLangFilter(''); setSortBy('recent'); }}
                className="w-full text-xs font-bold text-slate-700 hover:text-slate-900 px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 transition-colors"
              >
                Reset filtri
              </button>
            )}
          </aside>
          )}

        {/* Mappa ('map') oppure griglia/lista scrollabile */}
        {viewMode === 'map' ? (
          <ExperienceMap items={filtered} className="flex-1 min-w-0" />
        ) : (
        <div
          data-cid="excursions-catalog-scroll"
          className="flex-1 min-w-0 premium-snap-noscrollbar overflow-y-auto px-1 pr-2 -mr-2 pb-10"
          style={{ maxHeight: 'calc(100vh - 150px)' }}
        >
          {filtered.length === 0 ? (
            <div className="rounded-2xl p-12 text-center text-slate-500 text-sm bg-slate-50 border border-slate-200">
              Nessuna esperienza corrisponde ai filtri.
            </div>
          ) : (
            <div
              className={
                viewMode === 'list'
                  ? 'flex flex-col gap-2'
                  : `grid grid-cols-1 sm:grid-cols-2 ${filtersOpen ? 'xl:grid-cols-3' : 'lg:grid-cols-3 xl:grid-cols-4'} gap-4`
              }
            >
              {visible.map((ex, _i) => {
                const cp = (ex as ExcursionItem & { client_price?: number }).client_price;
                const finalPrice = cp != null ? cp : Number(ex.price) || 0;
                const isFav = wishlist.has(ex.id);
                // Divider LUOGO quando cambia la location rispetto alla card precedente.
                const _loc = (ex.meeting_point || 'Altrove').trim();
                const _showDiv = _i === 0 || (visible[_i - 1].meeting_point || 'Altrove').trim() !== _loc;
                const divider = _showDiv ? (
                  <div className="col-span-full flex items-center gap-3 pt-3 first:pt-0 mb-1">
                    <span className="h-px flex-1 bg-slate-200" />
                    <span className="inline-flex items-center gap-1.5 text-slate-500 text-[11px] font-bold uppercase tracking-[0.18em]"><MapPin size={12} /> {_loc}</span>
                    <span className="h-px flex-1 bg-slate-200" />
                  </div>
                ) : null;

                if (viewMode === 'list') {
                  return (
                    <Fragment key={ex.id}>
                      {divider}
                    <Link
                      href={`/esperienze/${ex.slug}`}
                      data-cid={`catalogo-row-${ex.slug}`}
                      className="group relative flex items-center gap-3 rounded-xl overflow-hidden no-underline transition-all bg-white border border-slate-200 hover:border-cyan-300 hover:shadow-md p-2"
                    >
                      {ex.hero_image ? (
                        <div
                          className="relative shrink-0 w-24 h-16 sm:w-28 sm:h-20 rounded-lg bg-cover bg-center"
                          style={{ backgroundImage: `url(${ex.hero_image})` }}
                        >
                          {ex.is_premium && (
                            <span className="absolute top-1 left-1 inline-flex items-center px-1 py-px rounded-full bg-amber-400 text-slate-900 text-[8px] font-black uppercase tracking-wider shadow leading-none">
                              ★
                            </span>
                          )}
                        </div>
                      ) : (
                        <div className="shrink-0 w-24 h-16 sm:w-28 sm:h-20 rounded-lg bg-slate-100 flex items-center justify-center text-slate-400">
                          <CategoryIcon id={ex.category || 'charter-custom'} size={28} />
                        </div>
                      )}

                      <div className="flex-1 min-w-0">
                        <h4 data-cid={`catalogo-row-title-${ex.slug}`} className="text-slate-900 font-bold text-[14px] leading-snug truncate group-hover:text-cyan-700 transition-colors">{ex.title}</h4>
                        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-slate-500">
                          {ex.duration_minutes > 0 && (
                            <span className="inline-flex items-center gap-1">
                              <Clock size={10} /> {durationLabel(ex.duration_minutes)}
                            </span>
                          )}
                          {ex.meeting_point && (
                            <span className="inline-flex items-center gap-1 min-w-0">
                              <MapPin size={10} className="shrink-0" />
                              <span className="truncate">{ex.meeting_point}</span>
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="shrink-0 flex flex-col items-end pr-1">
                        {finalPrice > 0 ? (
                          <>
                            <span className="text-[8px] uppercase tracking-wider font-bold text-slate-400 leading-none">Da</span>
                            <span className="text-slate-900 font-bold text-sm leading-tight">{formatEUR(finalPrice)}</span>
                            <span className="text-[10px] font-bold text-slate-400 leading-none">{priceUnit(ex.pricing_mode)}</span>
                          </>
                        ) : (
                          <span className="text-slate-700 font-semibold text-xs">Su richiesta</span>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); wishlist.toggle(ex.id); }}
                        aria-pressed={isFav}
                        className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full border border-slate-200 hover:border-rose-300 hover:bg-rose-50 transition-all"
                        style={isFav ? { background: '#f43f5e', borderColor: '#f43f5e' } : undefined}
                      >
                        <Heart size={11} color={isFav ? '#fff' : '#475569'} fill={isFav ? '#fff' : 'none'} />
                      </button>
                    </Link>
                    </Fragment>
                  );
                }

                return (
                  <Fragment key={ex.id}>
                    {divider}
                  <Link
                    href={`/esperienze/${ex.slug}`}
                    data-cid={`catalogo-card-${ex.slug}`}
                    className="group relative flex flex-col rounded-2xl overflow-hidden no-underline transition-all hover:-translate-y-1 bg-white border border-slate-200 shadow-sm hover:shadow-lg"
                  >
                    {ex.hero_image ? (
                      <div
                        className="relative w-full aspect-[16/8] bg-cover bg-center"
                        style={{ backgroundImage: `url(${ex.hero_image})` }}
                      >
                        {ex.is_premium && (
                          <span className="absolute top-2.5 left-2.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-amber-300 to-amber-500 text-slate-900 text-[9px] font-black uppercase tracking-wider shadow">
                            ★ Premium
                          </span>
                        )}
                        {ex.duration_minutes > 0 && (
                          <span className="absolute bottom-2.5 left-2.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/55 backdrop-blur text-white text-[10px] font-semibold">
                            <Clock size={10} /> {durationLabel(ex.duration_minutes)}
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); wishlist.toggle(ex.id); }}
                          aria-pressed={isFav}
                          className="absolute top-2 right-2 inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/95 hover:bg-white border border-slate-200 shadow-sm transition-all"
                          style={isFav ? { background: '#f43f5e', borderColor: '#f43f5e' } : undefined}
                        >
                          <Heart size={13} color={isFav ? '#fff' : '#475569'} fill={isFav ? '#fff' : 'none'} />
                        </button>
                      </div>
                    ) : (
                      <div className="relative w-full aspect-[16/8] bg-slate-100 flex items-center justify-center text-slate-400">
                        <CategoryIcon id={ex.category || 'charter-custom'} size={56} />
                      </div>
                    )}

                    <div className="flex-1 flex flex-col p-3">
                      <h4 data-cid={`catalogo-card-title-${ex.slug}`} className="text-slate-900 font-bold text-[15px] leading-tight line-clamp-2 mb-1.5 group-hover:text-cyan-700 transition-colors">{ex.title}</h4>
                      <div className="mb-1.5"><RatingChip rating={ex.rating} count={ex.reviews_count} /></div>
                      {ex.next_departure && (
                        <span data-cid={`catalogo-card-next-${ex.slug}`} className="inline-flex items-center gap-1 self-start mb-2 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-bold">
                          <Clock size={10} /> Prossima: {fmtNextDep(ex.next_departure)}{ex.next_departure.seats != null ? ` · ${ex.next_departure.seats} posti` : ''}
                        </span>
                      )}
                      {ex.meeting_point && (
                        <div className="inline-flex items-center gap-1 text-slate-500 text-[11px] mb-2 min-w-0">
                          <MapPin size={11} className="shrink-0" />
                          <span className="truncate">{ex.meeting_point}</span>
                        </div>
                      )}
                      <RichChips ex={ex} />
                      <div className="mt-auto flex items-end justify-between gap-2 pt-2 border-t border-slate-100">
                        <div className="flex flex-col">
                          {finalPrice > 0 ? (
                            <>
                              <span className="text-[9px] uppercase tracking-wider font-bold text-slate-400 leading-none">Da</span>
                              <span className="text-slate-900 font-bold text-base leading-tight">{formatEUR(finalPrice)}</span>
                              <span className="text-[10px] font-bold text-slate-400 leading-none">{priceUnit(ex.pricing_mode)}</span>
                            </>
                          ) : (
                            <span className="text-slate-900 font-semibold text-sm">Su richiesta</span>
                          )}
                        </div>
                        <span className="inline-flex items-center gap-1 text-cyan-700 font-bold text-xs">
                          Scopri <ArrowRight size={11} />
                        </span>
                      </div>
                    </div>
                  </Link>
                  </Fragment>
                );
              })}
            </div>
          )}
          {hasMore && (
            <div className="mt-6 flex justify-center">
              <button
                type="button"
                data-cid="esperienze-catalogo-load-more"
                onClick={() => setPage((p) => p + 1)}
                className="px-6 py-3 rounded-xl bg-slate-900 text-white text-sm font-bold hover:bg-slate-800 transition-colors"
              >
                Carica altre ({filtered.length - visible.length})
              </button>
            </div>
          )}
        </div>
        )}
      </div>
      </div>
    </section>
  );
}
